import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Pencil, Upload, PenTool, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  getFirmas,
  createFirma,
  updateFirma,
  deleteFirma,
  uploadFirmaImagen,
} from './Services/parametrizacion.services';

const MAX_MB = 2;

const FirmasPage = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', cargo: '', imagen: '' });
  const [preview, setPreview] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getFirmas();
      setItems(res?.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', cargo: '', imagen: '' });
    setPreview('');
    setArchivo(null);
    setOpen(true);
  };

  const abrirEdicion = (item) => {
    setEditando(item);
    setForm({ nombre: item.nombre || '', cargo: item.cargo || '', imagen: item.imagen || '' });
    setPreview(item.imagen || '');
    setArchivo(null);
    setOpen(true);
  };

  const elegirArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: 'Archivo muy grande',
        description: `La imagen debe pesar menos de ${MAX_MB} MB.`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    setArchivo(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast({
        title: 'Faltan datos',
        description: 'El nombre de quien firma es obligatorio.',
        variant: 'destructive',
      });
      return;
    }
    if (!archivo && !form.imagen) {
      toast({
        title: 'Falta la imagen',
        description: 'Sube la imagen de la firma antes de guardar.',
        variant: 'destructive',
      });
      return;
    }

    setGuardando(true);
    try {
      let imagen = form.imagen;
      // La imagen se sube primero y se guarda la URL devuelta, no el archivo:
      // así el registro nunca queda apuntando a un archivo que no se subió.
      if (archivo) {
        const res = await uploadFirmaImagen(archivo);
        if (!res?.state) throw new Error(res?.message || 'No se pudo subir la imagen');
        imagen = res.data?.url;
      }

      const payload = { ...form, imagen };
      if (editando) {
        await updateFirma(editando._id, payload);
        toast({ title: 'Éxito', description: 'Firma actualizada.' });
      } else {
        await createFirma(payload);
        toast({ title: 'Éxito', description: 'Firma creada.' });
      }
      setOpen(false);
      cargar();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await deleteFirma(aEliminar._id);
      toast({ title: 'Éxito', description: 'Firma eliminada.' });
      setAEliminar(null);
      cargar();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={abrirNuevo}>
            <Plus className="mr-2 h-4 w-4" /> Agregar firma
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="rounded-lg border p-10 text-center">
            <div className="flex justify-center mb-3">
              <div className="p-3 rounded-full bg-primary/15">
                <PenTool className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="font-medium">Todavía no hay firmas cargadas</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Estas firmas se estampan en los certificados laborales, los llamados de atención y las
              sanciones. Sube la imagen de la firma con fondo transparente para que se vea bien en
              el PDF.
            </p>
            <Button className="mt-4" onClick={abrirNuevo}>
              <Plus className="mr-2 h-4 w-4" /> Agregar firma
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item) => (
              <div key={item._id} className="rounded-lg border bg-card p-4 space-y-3">
                <div className="h-28 rounded-md border bg-white flex items-center justify-center overflow-hidden">
                  {item.imagen ? (
                    <img
                      src={item.imagen}
                      alt={`Firma de ${item.nombre}`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <PenTool className="h-8 w-8 text-gray-300" />
                  )}
                </div>

                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.nombre}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.cargo || 'Sin cargo'}
                    </p>
                  </div>
                  <Badge variant={item.activo ? 'default' : 'outline'}>
                    {item.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>

                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="icon" onClick={() => abrirEdicion(item)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => setAEliminar(item)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar firma' : 'Agregar firma'}</DialogTitle>
            <DialogDescription>
              La imagen se guarda en el servidor y se usará al firmar documentos del módulo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={guardar} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firma-nombre">Nombre de quien firma</Label>
              <Input
                id="firma-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej. María Fernanda Gómez"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="firma-cargo">Cargo</Label>
              <Input
                id="firma-cargo"
                value={form.cargo}
                onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                placeholder="Ej. Gerente de Recursos Humanos"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="firma-imagen">Imagen de la firma</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="firma-imagen"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={elegirArchivo}
                  className="h-auto py-2"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                PNG, JPG o WEBP de hasta {MAX_MB} MB. Ideal con fondo transparente.
              </p>
            </div>

            {preview && (
              <div className="h-28 rounded-md border bg-white flex items-center justify-center overflow-hidden">
                <img src={preview} alt="Vista previa" className="max-h-full max-w-full object-contain" />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={guardando}>
                Cancelar
              </Button>
              <Button type="submit" disabled={guardando}>
                {guardando ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                {editando ? 'Guardar cambios' : 'Agregar firma'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!aEliminar} onOpenChange={(o) => !o && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta firma?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la firma de “{aEliminar?.nombre}”. Los documentos ya generados no cambian,
              pero los nuevos saldrán sin esa firma disponible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarEliminar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default FirmasPage;